import { useQuery } from '@tanstack/react-query';
import { analyticsAPI } from '../../../lib/api';
import type { DateRange } from './useAnalyticsData';

const STALE_TIME_5MIN = 5 * 60 * 1000;
const GC_TIME = 30 * 60 * 1000;

export interface AgentPerformanceItem {
  userId: string;
  userName: string;
  leadsAssigned: number;
  leadsConverted: number;
  conversionRate: number | null;
  avgCycleTimeDays: number | null;
}

export interface AgentLeadCoverage {
  totalLeads: number;
  assignedLeads: number;
  unassignedLeads: number;
}

export interface AgentPerformanceResponse {
  agents: AgentPerformanceItem[];
  coverage: AgentLeadCoverage;
}

export interface UntouchedLeadsResponse {
  count: number | null;
  percentage: number | null;
  eligibleLeads: number;
  gracePeriodDays: number;
  leads: Array<{
    id: string;
    fullName: string;
    email: string;
    createdAt: string;
    daysSinceCreated: number;
  }>;
}

export interface ResponseTimeResponse {
  avgMinutes: number | null;
  medianMinutes: number | null;
  // The averages describe the leads that were actually CONTACTED, not every
  // lead in the window. Stated on the card so the number reconciles: `0m` used
  // to sit beside "Covers 0 of 4 leads" explaining nothing.
  respondedLeads: number;
  totalLeads: number;
  distribution: Array<{ range: string; count: number }>;
}

function toNum(v: unknown): number {
  if (typeof v === 'number') return v;
  return Number.parseFloat(String(v)) || 0;
}

// Null is a value in the analytics contract, not a missing number. Coercing it
// to 0 here would put back the fabrication the backend just stopped emitting.
function toNullableNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const parsed = toNum(v);
  return Number.isNaN(parsed) ? null : parsed;
}

export function useAgentPerformance(
  organizationId: string | undefined,
  dateRange: DateRange,
  userId?: string,
) {
  return useQuery<AgentPerformanceResponse>({
    queryKey: ['analytics', 'team', 'performance', organizationId, dateRange, userId],
    queryFn: async () => {
      const { data } = await analyticsAPI.getAgentPerformance({
        organizationId: organizationId!,
        startDate: dateRange.start,
        endDate: dateRange.end,
        userId,
      });
      const response = data as AgentPerformanceResponse;
      return {
        agents: response.agents.map((item) => ({
          ...item,
          leadsAssigned: toNum(item.leadsAssigned),
          leadsConverted: toNum(item.leadsConverted),
          conversionRate: toNullableNum(item.conversionRate),
          avgCycleTimeDays: toNullableNum(item.avgCycleTimeDays),
        })),
        coverage: {
          totalLeads: toNum(response.coverage.totalLeads),
          assignedLeads: toNum(response.coverage.assignedLeads),
          unassignedLeads: toNum(response.coverage.unassignedLeads),
        },
      };
    },
    enabled: !!organizationId,
    staleTime: STALE_TIME_5MIN,
    gcTime: GC_TIME,
  });
}

// No date range, in the key or the request. Untouched-ness is a state of the
// pipeline, so the answer does not change when the selector does — keying it by
// the range would only refetch an identical number under four cache entries.
export function useUntouchedLeads(organizationId: string | undefined) {
  return useQuery<UntouchedLeadsResponse>({
    queryKey: ['analytics', 'engagement', 'untouched', organizationId],
    queryFn: async () => {
      const { data } = await analyticsAPI.getUntouchedLeads({
        organizationId: organizationId!,
      });
      return {
        ...data,
        count: toNullableNum(data.count),
        percentage: toNullableNum(data.percentage),
        eligibleLeads: toNum(data.eligibleLeads),
        gracePeriodDays: toNum(data.gracePeriodDays),
      };
    },
    enabled: !!organizationId,
    staleTime: STALE_TIME_5MIN,
    gcTime: GC_TIME,
  });
}

export function useResponseTime(organizationId: string | undefined, dateRange: DateRange) {
  return useQuery<ResponseTimeResponse>({
    queryKey: ['analytics', 'engagement', 'response-time', organizationId, dateRange],
    queryFn: async () => {
      const { data } = await analyticsAPI.getResponseTime({
        organizationId: organizationId!,
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
      return {
        ...data,
        avgMinutes: toNullableNum(data.avgMinutes),
        medianMinutes: toNullableNum(data.medianMinutes),
        respondedLeads: toNum(data.respondedLeads),
        totalLeads: toNum(data.totalLeads),
      };
    },
    enabled: !!organizationId,
    staleTime: STALE_TIME_5MIN,
    gcTime: GC_TIME,
  });
}
